import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExchangeAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import warehouseService from '../../services/warehouseService';
import productSizesService from '../../services/productSizesServices';
import validateUtils from '../../utils/validate';

// Cache for warehouse and product options to avoid repeated API calls
if (!window.warehouseInventoryTransferFormCache) {
  window.warehouseInventoryTransferFormCache = {
    warehouses: null,
    products: null,
  };
}

const WarehouseInventoryTransferForm = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  onSubmit, 
  loading = false
}) => {
  const { t } = useTranslation();
  const strings = t('warehouseInventory.transferForm', { returnObjects: true });
  const fields = strings.fields;
  const REQUIRED = t('common.form.requiredIndicator');
  const cache = window.warehouseInventoryTransferFormCache;

  // State for loading options
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Dropdown options
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  // Normalize close callback
  const closeHandler = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof onCancel === 'function') return onCancel();
    return undefined;
  }, [onClose, onCancel]);

  const [form, setForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    product_size_id: '',
    quantity: '',
    reason: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch dropdown options once
  const fetchDropdownOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const promises = [];
      
      // Fetch warehouses if not cached
      if (!cache.warehouses) {
        promises.push(
          warehouseService.getAllWarehouses().then(result => {
            if (result.success && Array.isArray(result.data)) {
              cache.warehouses = result.data.map(w => ({
                value: w.id,
                label: w.name || `Warehouse ${w.id}`,
              }));
            } else {
              cache.warehouses = [];
            }
          }).catch(() => {
            cache.warehouses = [];
          })
        );
      }

      // Fetch product sizes if not cached
      if (!cache.products) {
        promises.push(
          productSizesService.getAllProductSizes({}, { force: false }).then((res) => {
            if (res && res.success && Array.isArray(res.data)) {
              cache.products = res.data.map((p) => ({
                value: p.id,
                label: p.alias_name || p.product_size || `Product ${p.id}`,
                // Carried so the picker can grey these out: stock for a coil-tracked
                // size lives in the coil ledger, and the backend refuses a transfer.
                coil_tracked: Boolean(p.coil_tracked),
                // Carried so the quantity field can be labelled in pieces for a
                // pieces-tracked size (its stock is a count, not kg).
                track_by_pieces: Boolean(p.track_by_pieces),
              }));
            } else {
              cache.products = [];
            }
          }).catch(() => {
            cache.products = [];
          })
        );
      }

      await Promise.all(promises);

      setWarehouseOptions(cache.warehouses || []);
      setProductOptions(cache.products || []);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    } finally {
      setOptionsLoading(false);
    }
  }, [cache]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setForm({
        from_warehouse_id: '',
        to_warehouse_id: '',
        product_size_id: '',
        quantity: '',
        reason: '',
      });
      setErrors({});
      
      // Fetch dropdown options if not already cached
      if (!cache.warehouses || !cache.products) {
        fetchDropdownOptions();
      } else {
        setWarehouseOptions(cache.warehouses);
        setProductOptions(cache.products);
      }
    }
  }, [isOpen, fetchDropdownOptions, cache]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    
    // Apply input filters for numeric fields
    if (name === 'quantity') {
      v = validateUtils.inputFilters.numeric(value, false); // Don't allow negative for transfers
    }
    
    setForm(prev => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const v = {};
    
    // From warehouse is required
    if (!form.from_warehouse_id) {
      v.from_warehouse_id = fields.from_warehouse_id.errors.required;
    }
    
    // To warehouse is required
    if (!form.to_warehouse_id) {
      v.to_warehouse_id = fields.to_warehouse_id.errors.required;
    }
    
    // From and to warehouse cannot be the same
    if (form.from_warehouse_id && form.to_warehouse_id && form.from_warehouse_id === form.to_warehouse_id) {
      v.to_warehouse_id = fields.to_warehouse_id.errors.sameWarehouse;
    }
    
    // Product is required
    if (!form.product_size_id) {
      v.product_size_id = fields.product_size_id.errors.required;
    }
    
    // Quantity is required and must be positive
    if (!form.quantity || !String(form.quantity).trim()) {
      v.quantity = fields.quantity.errors.required;
    } else if (isNaN(Number(form.quantity)) || Number(form.quantity) <= 0) {
      v.quantity = fields.quantity.errors.invalid;
    }
    
    // Reason is required
    if (!form.reason || !String(form.reason).trim()) {
      v.reason = fields.reason.errors.required;
    }
    
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        from_warehouse_id: parseInt(form.from_warehouse_id, 10),
        to_warehouse_id: parseInt(form.to_warehouse_id, 10),
        product_size_id: parseInt(form.product_size_id, 10),
        quantity: parseFloat(form.quantity),
        reason: form.reason.trim(),
      };

      // Close only on success — keep the form (and the entered transfer qty /
      // warehouses / reason) when the backend rejects it. The parent shows the error.
      const res = await onSubmit(payload);
      if (!res || res.success) closeHandler();
    } catch (submitError) {
      console.error('Error submitting transfer form:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const scrollClass = 'warehouse-inventory-transfer-form-scroll';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-2xl relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {strings.title}
          </h2>
          <button 
            onClick={closeHandler} 
            className="p-2 rounded-lg hover:opacity-75 transition-opacity" 
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.buttons.loading}
                </p>
              </div>
            </div>
          )}
          
          {optionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.loading.options}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Warehouse selection row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* From warehouse dropdown */}
                <div>
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {fields.from_warehouse_id.label} {REQUIRED}
                  </label>
                  <select 
                    name="from_warehouse_id" 
                    value={form.from_warehouse_id} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 rounded-xl border-2" 
                    style={{ 
                      backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                      borderColor: errors.from_warehouse_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                    }} 
                  >
                    <option value="">{fields.from_warehouse_id.placeholder}</option>
                    {warehouseOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.from_warehouse_id && (
                    <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.from_warehouse_id}</div>
                  )}
                </div>

                {/* To warehouse dropdown */}
                <div>
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {fields.to_warehouse_id.label} {REQUIRED}
                  </label>
                  <select 
                    name="to_warehouse_id" 
                    value={form.to_warehouse_id} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 rounded-xl border-2" 
                    style={{ 
                      backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                      borderColor: errors.to_warehouse_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                    }} 
                  >
                    <option value="">{fields.to_warehouse_id.placeholder}</option>
                    {warehouseOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.to_warehouse_id && (
                    <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.to_warehouse_id}</div>
                  )}
                </div>
              </div>

              {/* Product dropdown */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {fields.product_size_id.label} {REQUIRED}
                </label>
                <select 
                  name="product_size_id" 
                  value={form.product_size_id} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 rounded-xl border-2" 
                  style={{ 
                    backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                    borderColor: errors.product_size_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                  }} 
                >
                  <option value="">{fields.product_size_id.placeholder}</option>
                  {productOptions.map(option => (
                    <option key={option.value} value={option.value} disabled={option.coil_tracked}>
                      {option.label}
                      {option.coil_tracked ? ` — ${t('coils.ledger.messages.notStockedHere')}` : ''}
                    </option>
                  ))}
                </select>
                {productOptions.some((o) => o.coil_tracked) && (
                  <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {t('coils.ledger.messages.notStockedHereHint')}
                  </p>
                )}
                {errors.product_size_id && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.product_size_id}</div>
                )}
              </div>

              {/* Quantity field */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {fields.quantity.label}{productOptions.find((o) => String(o.value) === String(form.product_size_id))?.track_by_pieces ? ` (${t('pieces.inventory.unitPieces')})` : ''} {REQUIRED}
                </label>
                <input 
                  name="quantity" 
                  value={form.quantity} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 rounded-xl border-2" 
                  placeholder={fields.quantity.placeholder} 
                  style={{ 
                    backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                    borderColor: errors.quantity ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                  }} 
                />
                {errors.quantity && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.quantity}</div>
                )}
              </div>

              {/* Reason field */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {fields.reason.label} {REQUIRED}
                </label>
                <textarea
                  name="reason" 
                  value={form.reason} 
                  onChange={handleChange} 
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 resize-none" 
                  placeholder={fields.reason.placeholder} 
                  style={{ 
                    backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                    borderColor: errors.reason ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                  }} 
                />
                {errors.reason && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.reason}</div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <button 
                  type="button"
                  onClick={closeHandler}
                  className="px-6 py-3 rounded-xl font-semibold" 
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, 
                    color: UI_COLORS.BUTTONS.SECONDARY.text 
                  }}
                >
                  {strings.buttons.cancel}
                </button>
                <button 
                  type="submit" 
                  disabled={loading || submitting} 
                  className="px-6 py-3 rounded-xl font-semibold" 
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                    color: UI_COLORS.BUTTONS.PRIMARY.text 
                  }}
                >
                  <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" /> 
                  {strings.buttons.submit}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default WarehouseInventoryTransferForm;